/**
 * The frame every signed-in page renders inside: a top bar, a sidebar and a
 * content region.
 *
 * # The layout decision
 *
 * The sidebar is 208px and the top bar is 44px, which is small for both. That
 * is the point: DESIGN_LANGUAGE.md §1 asks the interface to be optimized for
 * the tenth hour, and in the tenth hour the chrome is something the user reads
 * past. A 64px top bar costs 20 pixels of the thing they came for, on every
 * screen, forever.
 *
 * The content region scrolls; the sidebar and top bar do not. A sidebar that
 * scrolls with the page is a sidebar whose items move, and §1 asks for "stable
 * positions".
 */

import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useMemo, type ReactNode } from 'react';

import { useSessionContext } from '~/auth/session';
import {
  Count,
  Avatar,
  Badge,
  Icon,
  IconButton,
  Kbd,
  Menu,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuSeparator,
  Tooltip,
  commandPaletteHint,
  useCommandPalette,
  useRegisterCommands,
  type Command,
} from '~/components';
import { useAppearance } from '~/app/appearance';
import { useRecentRepositories } from '~/lib/recent';
import { useUnreadCount } from '~/api/phase2Queries';
import { cx } from '~/lib/cx';
import { NAVIGATION } from './navigation';

import styles from './AppShell.module.css';

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <div className={styles.shell}>
      {/* The first focusable thing on the page, and the reason a keyboard user
       * does not tab through six navigation items on every navigation. */}
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <TopBar />
      <Sidebar />
      <main id="main" className={styles.main} tabIndex={-1}>
        <div className={styles.content}>{children}</div>
      </main>
    </div>
  );
}

/* ====================================================================== *
 * Top bar
 * ====================================================================== */

function TopBar() {
  const { user, orgId, signOut, isSigningOut } = useSessionContext();
  const palette = useCommandPalette();
  const navigate = useNavigate();

  return (
    <header className={styles.topBar}>
      <Link to="/" className={styles.brand}>
        <Icon.Mark size={18} className={styles.brandMark} />
        <span className={styles.brandName}>Exi Git</span>
      </Link>

      {/* A button and not an input. It opens the palette, which has its own
       * input; two text fields that look the same and behave differently is
       * the thing that makes a search box feel broken. */}
      <button type="button" className={styles.search} onClick={palette.open}>
        <Icon.Search size={14} />
        <span className={styles.searchLabel}>Search pages and actions</span>
        <Kbd>{commandPaletteHint()}</Kbd>
      </button>

      <div className={styles.topBarEnd}>
        <AppearanceMenu />

        <Menu
          trigger={
            <button type="button" className={styles.account} aria-label="Account menu">
              <Avatar name={user?.display_name ?? user?.username ?? '?'} size="sm" />
              <Icon.ChevronDown size={12} />
            </button>
          }
        >
          <MenuLabel>{user?.display_name ?? user?.username ?? 'Signed in'}</MenuLabel>
          <div className={styles.menuEmail}>{user?.primary_email}</div>
          <MenuSeparator />
          <MenuItem
            icon={<Icon.Person />}
            onSelect={() => {
              void navigate({ to: '/settings' });
            }}
          >
            Account settings
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            icon={<Icon.SignOut />}
            tone="danger"
            disabled={isSigningOut}
            onSelect={() => {
              void signOut();
            }}
          >
            Sign out
          </MenuItem>
        </Menu>

        {orgId ? null : (
          // Only reachable if `personal_org_id` was absent from `/users/me`,
          // which the schema permits. Said plainly rather than letting every
          // page render an unexplained empty state.
          <Tooltip content="Your account has no personal organization, so organization-scoped pages cannot load.">
            <span>
              <Badge tone="warning">No organization</Badge>
            </span>
          </Tooltip>
        )}
      </div>
    </header>
  );
}

function AppearanceMenu() {
  const { theme, setTheme, density, setDensity } = useAppearance();

  return (
    <Menu
      trigger={
        <span>
          <IconButton label="Appearance" icon={<Icon.Sun />} size="sm" />
        </span>
      }
    >
      <MenuRadioGroup
        label="Theme"
        value={theme}
        onValueChange={(value) => {
          setTheme(value as 'light' | 'dark' | 'system');
        }}
        options={[
          { value: 'light', label: 'Light', icon: <Icon.Sun size={14} /> },
          { value: 'dark', label: 'Dark', icon: <Icon.Moon size={14} /> },
          { value: 'system', label: 'System', icon: <Icon.Monitor size={14} /> },
        ]}
      />
      <MenuSeparator />
      <MenuRadioGroup
        label="Density"
        value={density}
        onValueChange={(value) => {
          setDensity(value as 'compact' | 'comfortable');
        }}
        options={[
          { value: 'compact', label: 'Compact', icon: <Icon.Rows size={14} /> },
          { value: 'comfortable', label: 'Comfortable', icon: <Icon.Rows size={14} /> },
        ]}
      />
    </Menu>
  );
}

/* ====================================================================== *
 * Sidebar
 * ====================================================================== */

function Sidebar() {
  const routerState = useRouterState();
  const current = routerState.location.pathname;
  const palette = useCommandPalette();
  const navigate = useNavigate();
  const { orgId } = useSessionContext();
  // The server's count, which leaves out anything the caller can no longer
  // see. Absent rather than zero while it loads or if it fails: the badge is
  // a convenience, and the inbox itself says what is there.
  const unread = useUnreadCount(orgId);

  // The navigation is also the bulk of the command palette. Registered here
  // rather than in the palette itself so there is one list, in one file, and a
  // new destination appears in both without being added twice.
  const recent = useRecentRepositories();
  const commands = useMemo<Command[]>(
    () => [
      // Recently opened repositories first (Phase 4P): what a person reaches
      // for most is what they were just looking at. This browser's list.
      ...recent.map((r) => ({
        id: `recent:${r.orgId}/${r.repoId}`,
        group: 'Recent repositories',
        label: r.name,
        keywords: ['repository', 'recent', r.org ?? ''],
        icon: <Icon.Repository size={14} />,
        run: () => {
          void navigate({ to: `/repositories/${r.orgId}/${r.repoId}` });
        },
      })),
      ...NAVIGATION.map((item) => ({
        id: `go:${item.to}`,
        group: 'Go to',
        label: item.label,
        keywords: [item.description],
        icon: <item.icon size={14} />,
        run: () => {
          // Router navigation, not `location.assign`: a full page load here
          // would throw away the query cache and the session, which is a
          // second of blank screen for a keystroke that should be instant.
          void navigate({ to: item.to });
        },
      })),
    ],
    [navigate, recent],
  );
  useRegisterCommands(commands);

  return (
    <nav className={styles.sidebar} aria-label="Primary">
      <ul className={styles.navList}>
        {NAVIGATION.map((item) => {
          const active = item.to === '/' ? current === '/' : current.startsWith(item.to);
          return (
            <li key={item.to}>
              <Link
                to={item.to}
                className={cx(styles.navItem, active && styles.navItemActive)}
                // `aria-current` and not only a colour: §8 forbids conveying
                // state by appearance alone, and the accent rule on the left
                // edge below is the visual half of the same answer.
                {...(active ? { 'aria-current': 'page' as const } : {})}
              >
                <item.icon size={16} className={styles.navIcon} />
                <span className={styles.navLabel}>{item.label}</span>
                {item.to === '/inbox' && unread.data ? (
                  <span className={styles.navBadge}>
                    <Count value={unread.data} tone="accent" label="unread notifications" />
                  </span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>

      <div className={styles.sidebarFoot}>
        <button type="button" className={styles.footHint} onClick={palette.open}>
          <Icon.Search size={12} />
          <span>Press</span>
          <Kbd>{commandPaletteHint()}</Kbd>
          <span>anywhere</span>
        </button>
      </div>
    </nav>
  );
}
